import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import * as React from 'react';
import { ITemplate, TemplateScope } from '../../../extViewmodels';
import {
	ICreateSocialMediaPostRequest,
	IImpersonationContextComponentProps,
	ILocationState,
	ImpersonationContextKey,
} from '../../../models';
import { CampaignType, IEditHtmlNewsletterRequest } from '../../../models/AdminModels';
import { BlogPostRequest } from '../../../models/Blogs';
import {
	useEnvironment,
	useErrorMessages,
	useFullscreenModal,
	useUserSession,
} from '../../../models/hooks/appStateHooks';
import { useTemplateExportMutation } from '../../../queries';
import { HtmlNewsletterViewModel } from '../../../viewmodels/HtmlNewletterViewModels';
import { AiButton } from '../../components-v2/AiButton';
import { Button } from '../../components-v2/Button';
import { ButtonGroup, ButtonGroupSeparator } from '../../components-v2/ButtonGroup';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../components-v2/Dropdown';
import { SupportedCampaignTypes } from '../../containers/ContentCalendar/models';
import { bs } from '../../styles/styles';
import { ExportConfirmationModalV2 } from '../ExportConfirmation';
import { ISelectBoxOption } from '../SelectBox';
import { ChevronDown } from '../svgs/icons/ChevronDown';
import { styleSheet } from './styles';

type CreateActionKey = 'email' | 'social';

interface IProps {
	className?: string;
	primaryAction?: CreateActionKey;
}

export const NewCampaignButtons: React.FC<IProps> = ({ primaryAction = 'email' }) => {
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const templateExportMutation = useTemplateExportMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			templateExportMutation.reset();
		},
	});

	const onCreateCampaign = () => {
		fullscreenModal.history.push({
			pathname: '/email/campaigns/create/from-scratch',
		});
	};

	const onCreateAICampaign = () => {
		fullscreenModal.history.push({ pathname: '/email/campaigns/create/ai' });
	};

	const onCreateSocialPost = () => {
		fullscreenModal.history.push({ pathname: '/social-media/post/create' });
	};

	const onExportTemplates = () => {
		if (templateExportMutation.isIdle) {
			templateExportMutation.mutate({});
		}
	};

	const socialEnabled =
		userSession?.account?.features?.socialMedia?.enabled &&
		userSession?.user?.userGroupPermissions?.featurePermissions?.socialMedia?.send;
	// Inert without content generation, so both flags are required, matching the server-side gate.
	const aiCampaignEnabled =
		!!userSession?.account?.features?.aiEmailCampaign?.enabled &&
		!!userSession?.account?.features?.contentGeneration?.enabled;
	const templateExportEnabled = userSession?.account?.features?.crossAccountTemplateSharing?.enabled;

	const createActions: { key: CreateActionKey; label: string; onSelect: () => void }[] = [];
	if (socialEnabled) {
		createActions.push({ key: 'social', label: 'Create Social Media Post', onSelect: onCreateSocialPost });
	}
	createActions.unshift({
		key: 'email',
		label: createActions.length ? 'Create Email Campaign' : 'Create New Campaign',
		onSelect: onCreateCampaign,
	});

	const primary = createActions.find(action => action.key === primaryAction) ?? createActions[0];
	const overflowActions = createActions.filter(action => action !== primary);

	return (
		<>
			<div className={css(styleSheet.buttonContainer)}>
				<ButtonGroup>
					<Button onClick={primary.onSelect}>{primary.label}</Button>
					{overflowActions.length ? (
						<>
							<ButtonGroupSeparator />
							<Dropdown>
								<DropdownTrigger render={<Button aria-label='More ways to create' />}>
									<ChevronDown />
								</DropdownTrigger>
								<DropdownContent align='end' styles={[bs.w56]}>
									{overflowActions.map(action => (
										<DropdownItem key={action.key} onClick={action.onSelect}>
											{action.label}
										</DropdownItem>
									))}
								</DropdownContent>
							</Dropdown>
						</>
					) : null}
				</ButtonGroup>
				{aiCampaignEnabled && <AiButton title='Create AI Email Campaign' onClick={onCreateAICampaign} />}
				{templateExportEnabled && (
					<Button variant='secondary' onClick={onExportTemplates} disabled={!templateExportMutation.isIdle}>
						Export Templates
					</Button>
				)}
			</div>
			{!templateExportMutation.isIdle && (
				<ExportConfirmationModalV2
					mutationResult={templateExportMutation}
					modalProps={{
						isOpen: !templateExportMutation.isIdle,
						onRequestClose: () => templateExportMutation.reset(),
					}}
					title='Export Templates to CSV'
					documentKind='template export'
					subTitle={
						<>
							<div>The export includes template name, type, keywords,</div>
							<div>creation date, and template ID for all your templates.</div>
						</>
					}
				/>
			)}
		</>
	);
};

interface IDropDownProps extends IImpersonationContextComponentProps {
	className?: string;
	supportedCampaignTypes: CampaignType[];
	selectedCampaignType?: CampaignType;
	dropdownDisabled?: boolean;
}

export const NewCampaignButtonsDropDown = inject(ImpersonationContextKey)(function NewCampaignButtonsDropDown({
	impersonationContext,
	supportedCampaignTypes = SupportedCampaignTypes,
	selectedCampaignType,
	dropdownDisabled,
}: IDropDownProps) {
	const isAdminView = useEnvironment()?.appType === 'admin';

	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const templateExportMutation = useTemplateExportMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			templateExportMutation.reset();
		},
	});

	const account = impersonationContext?.isValid ? impersonationContext.account : userSession.account;

	const options = React.useMemo<ISelectBoxOption<CampaignType>[]>(() => {
		const o: ISelectBoxOption<CampaignType>[] = [];
		if (supportedCampaignTypes.includes(CampaignType.Email)) {
			o.push({
				title: 'Email',
				value: CampaignType.Email,
			});
		}
		if (supportedCampaignTypes.includes(CampaignType.Social) && account?.features?.socialMedia?.enabled) {
			o.push({
				title: 'Social Post',
				value: CampaignType.Social,
			});
		}
		if (supportedCampaignTypes.includes(CampaignType.Blog) && account?.features?.blogFeature?.enabled) {
			o.push({
				title: 'Blog Post',
				value: CampaignType.Blog,
			});
		}
		if (
			supportedCampaignTypes.includes(CampaignType.htmlNewsletter) &&
			isAdminView &&
			account?.features?.htmlNewsletter?.enabled
		) {
			o.push({
				title: 'HTML newsletter',
				value: CampaignType.htmlNewsletter,
			});
		}
		return o;
	}, [
		account?.features?.blogFeature?.enabled,
		account?.features?.htmlNewsletter?.enabled,
		account?.features?.socialMedia?.enabled,
		isAdminView,
		supportedCampaignTypes,
	]);

	const [selectedContentType, setSelectedContentType] = React.useState<CampaignType | null>(null);

	React.useEffect(() => {
		if (selectedCampaignType) {
			setSelectedContentType(selectedCampaignType);
		}
	}, [selectedCampaignType]);

	const defaultOption = options[0];
	const currentOption = selectedContentType
		? options.find(opt => opt.value === selectedContentType) || defaultOption
		: defaultOption;

	const getButtonText = (option: ISelectBoxOption<CampaignType>) => {
		switch (option.value) {
			case CampaignType.Email:
				return 'Create Email Campaign';
			case CampaignType.Social:
				return 'Create Social Media Post';
			case CampaignType.Blog:
				return 'Create Blog Post';
			case CampaignType.htmlNewsletter:
				return 'Create HTML Newsletter';
			default:
				return 'Create New Campaign';
		}
	};

	const navigateToPath = (path: string, state?: any) => {
		fullscreenModal?.history.push({ pathname: path, state });
	};

	const onSelectionChanged = (option: ISelectBoxOption<any>) => {
		setSelectedContentType(option.value);
		switch (option.value) {
			case CampaignType.Social:
				onCreateSocialPost();
				break;
			case CampaignType.htmlNewsletter:
				onCreateHtmlNewsletter();
				break;
			case CampaignType.Email:
				onCreateCampaign();
				break;
			case CampaignType.Blog:
				onCreateBlogPost();
				break;
			default:
				break;
		}
	};

	const onPrimaryClick = () => {
		if (currentOption) {
			onSelectionChanged(currentOption);
		}
	};

	const onCreateHtmlNewsletter = async () => {
		if (isAdminView && impersonationContext?.account?.features?.htmlNewsletter?.enabled) {
			const template = await HtmlNewsletterViewModel.createDefaultBlankTemplate(userSession, {
				name: 'Untitled Newsletter',
				scope: TemplateScope.Account,
			});

			const locationState: ILocationState<any, IEditHtmlNewsletterRequest> = {
				model: {
					impersonationContext,
					type: 'Template',
					template,
				},
			};
			fullscreenModal?.history.push({
				pathname: `/accounts/${impersonationContext?.account?.id}/htmlnewsletter/new`,
				state: locationState,
			});
			return;
		}
	};

	const onCreateBlogPost = () => {
		if (isAdminView) {
			const locationState: ILocationState<any, BlogPostRequest> = {
				model: {
					impersonationContext,
				},
			};
			navigateToPath(`/accounts/${impersonationContext?.account?.id}/content-calendar/blog/post/create`, locationState);
			return;
		}
		navigateToPath('/blog/post/create');
	};

	const onCreateCampaign = () => {
		if (isAdminView) {
			navigateToPath(`/accounts/${impersonationContext?.account?.id}/content-calendar/campaigns/create`);
			return;
		}
		navigateToPath('/email/campaigns/create/from-scratch');
	};

	const onCreateSocialPost = () => {
		if (isAdminView) {
			const locationStateSocial: ILocationState<any, ICreateSocialMediaPostRequest<ITemplate>> = {
				model: {
					context: {},
					impersonationContext,

					sendFromUser: impersonationContext.user,
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: `/accounts/${impersonationContext?.account?.id}/content-calendar/post/create`,
				state: locationStateSocial,
			});
			return;
		}

		navigateToPath('/social-media/post/create');
	};

	const onExportTemplates = () => {
		if (templateExportMutation.isIdle) {
			templateExportMutation.mutate({});
		}
	};

	const templateExportEnabled = account?.features?.crossAccountTemplateSharing?.enabled;

	return (
		<>
			<div className={css(styleSheet.buttonContainer)}>
				<ButtonGroup>
					<Button onClick={onPrimaryClick}>{getButtonText(currentOption)}</Button>
					{options.length > 1 && !dropdownDisabled ? (
						<>
							<ButtonGroupSeparator />
							<Dropdown>
								<DropdownTrigger render={<Button />}>
									<ChevronDown />
								</DropdownTrigger>
								<DropdownContent align='end' styles={[bs.w56]}>
									{options
										.filter(option => option.value !== currentOption?.value)
										.map(option => (
											<DropdownItem key={option.value} onClick={() => onSelectionChanged(option)}>
												{getButtonText(option)}
											</DropdownItem>
										))}
								</DropdownContent>
							</Dropdown>
						</>
					) : null}
				</ButtonGroup>
				{templateExportEnabled && (
					<Button
						variant='secondary'
						size='default'
						onClick={onExportTemplates}
						disabled={!templateExportMutation.isIdle}
					>
						Export Templates
					</Button>
				)}
			</div>
			{!templateExportMutation.isIdle && (
				<ExportConfirmationModalV2
					mutationResult={templateExportMutation}
					modalProps={{
						isOpen: !templateExportMutation.isIdle,
						onRequestClose: () => templateExportMutation.reset(),
					}}
					title='Export Templates to CSV'
					documentKind='template export'
					subTitle={
						<>
							<div>The export includes template name, type, keywords,</div>
							<div>creation date, and template ID for all your templates.</div>
						</>
					}
				/>
			)}
		</>
	);
});
